import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { flatMap } from 'lodash-es';
import * as moment from 'moment';
import {
    RegistrationDetailService,
    Session,
    SessionService,
} from '../../../../../_services';
import { DownloadWithToastsService } from '../../../../_shared/download-with-toasts';
import { ToasterService } from '../../../../_shared/toaster';

declare const navigator: any;

const documentResolver = {
    ATTENDANCE_SHEET_EMPTY: (sessionId: number) =>
        `/api/training-sessions/${sessionId}/documents.pdf/?docs=ATTENDANCE_SHEET_EMPTY&template=ZENIKA`,
    EVALUATION_EMPTY: (sessionId: number) =>
        `/api/training-sessions/${sessionId}/documents.pdf/?docs=EVALUATION_EMPTY&template=ZENIKA`,
    STUDENTS_CSV: (sessionId: number) =>
        `/api/training-sessions/${sessionId}/students-csv`,
};

@Component({
    selector: 'document-generation',
    templateUrl: 'document-generation.component.html',
    styleUrls: [
        '../registration.component.scss',
        '../../../../_shared/label/label.component.scss',
    ],
})
export class DocumentGenerationComponent implements OnInit {
    @Input() session: Session;
    @Input() sessionId;
    @Input() tsCompanies;
    @Input() isOneElementSelected;
    @Input() areAllSelected;
    @ViewChild('sendTrainingSessionRecapToContactModal')
    sendTrainingSessionRecapToContactModal: any;

    generateList: {
        label: string;
        id: string;
        requiresLocation: boolean;
    }[] = [];
    sendList: { label: string; id: string; requiresLocation: boolean }[] = [];
    templateList: [string, string][] = [];

    dropdownCheckboxes: any = {};

    htmlEmail: string;
    displayHtmlEmailModal = false;

    quillConfig = {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ header: 1 }, { header: 2 }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ script: 'sub' }, { script: 'super' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ size: ['small', false, 'large', 'huge'] }],
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                [{ align: [] }],
                ['clean'],
            ],
        },
    };

    constructor(
        private toasterService: ToasterService,
        private sessionService: SessionService,
        private registrationDetailService: RegistrationDetailService,
        private downloadService: DownloadWithToastsService,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.initGenerateList();
        this.initSendList();
        this.initDropdownCheckboxes();

        this.registrationDetailService.isOneStudentSelected.subscribe(
            (item) => {
                this.isOneElementSelected = item;
            }
        );

        this.registrationDetailService.areAllSelected.subscribe((item) => {
            this.areAllSelected = item;
        });
    }

    initDropdownCheckboxes(): void {
        const sessionHasLocation = this.session.location !== 'À définir';
        const messageIfMissingLocation =
            'session-details.student-tab.documentGenerationNotAvailableBecauseNoLocation';

        this.dropdownCheckboxes.generate = {};
        this.dropdownCheckboxes.send = {};

        this.generateList.forEach((doc) => {
            const disabled = doc.requiresLocation && !sessionHasLocation;
            this.dropdownCheckboxes.generate[doc.id] = {
                checked: false,
                disabled,
                title: disabled ? messageIfMissingLocation : '',
            };
        });
        this.sendList.forEach((doc) => {
            const disabled = doc.requiresLocation && !sessionHasLocation;
            this.dropdownCheckboxes.send[doc.id] = {
                checked: false,
                disabled,
                title: disabled ? messageIfMissingLocation : '',
            };
        });
    }

    initGenerateList() {
        this.generateList = [
            {
                label: 'Emargement',
                id: 'ATTENDANCE_SHEET',
                requiresLocation: true,
            },
            { label: 'Convocation', id: 'CONVOCATION', requiresLocation: true },
            { label: 'Convention', id: 'CONVENTION', requiresLocation: true },
            {
                label: 'Attestation présence',
                id: 'ATTENDANCE_CERTIFICATE',
                requiresLocation: false,
            },
            {
                label: 'Attestation de fin de formation',
                id: 'TRAINING_CERTIFICATE',
                requiresLocation: false,
            },
        ];
    }

    initSendList() {
        this.sendList = [
            { label: 'Convocation', id: 'CONVOCATION', requiresLocation: true },
            { label: 'Convention', id: 'CONVENTION', requiresLocation: true },
            {
                label: 'Attestation de fin de formation',
                id: 'TRAINING_CERTIFICATE',
                requiresLocation: false,
            },
        ];
    }

    initTemplatesList() {
        this.templateList.push(['Zenika', 'ZENIKA']);
        this.templateList.push(['Rupture21', 'RUPTURE21']);
    }

    updateSelectedGeneration(doc, $event): void {
        if ($event) {
            $event.stopPropagation();
        }
        this.dropdownCheckboxes.generate[doc.id].checked =
            !this.dropdownCheckboxes.generate[doc.id].checked;
    }

    updateSelectedSend(doc, $event): void {
        if ($event) {
            $event.stopPropagation();
        }
        this.dropdownCheckboxes.send[doc.id].checked =
            !this.dropdownCheckboxes.send[doc.id].checked;
    }

    getGenerateDocsParams(): string {
        let res = '';
        res += this.buildDocsParams();
        res += this.buildRegistrationsParams();
        res += this.buildTemplateParams();
        return res;
    }

    buildDocsParams() {
        let res = '?docs=';
        let i = 0;
        this.generateList.forEach((doc) => {
            if (this.dropdownCheckboxes.generate[doc.id].checked) {
                res = i === 0 ? (res += doc.id) : (res += ',' + doc.id);
                ++i;
            }
        });
        return res;
    }

    buildRegistrationsParams() {
        let res = '&registrations=';

        const contacts = [].concat.apply(
            [],
            this.tsCompanies.map((company) => company.contacts)
        );
        const students = [].concat.apply(
            [],
            contacts.map((contact) => contact.students)
        );

        return (
            res +
            students
                .filter((student) => student.selected)
                .map((student) => student.id)
                .join(',')
        );
    }

    buildTemplateParams() {
        const template = 'ZENIKA';
        return '&template=' + template;
    }

    private resolveUrl(document: string, sessionId: number) {
        if (!(document in documentResolver)) {
            throw new Error(
                `Unknown document: expected one of ${Object.keys(
                    documentResolver
                ).join(', ')} but found '${document}'`
            );
        }
        return documentResolver[document](sessionId);
    }

    async downloadDocument(
        type: string,
        fileName: string,
        document?: string,
        sessionId?: number
    ) {
        const route =
            document && sessionId
                ? this.resolveUrl(document, sessionId)
                : this.getGeneratedRoute();
        await this.downloadService.downloadAndOpenDocument(
            route,
            type,
            fileName
        );
    }

    async copyStudentEmailToClipboard(): Promise<void> {
        const textToCopy = flatMap(
            flatMap(this.tsCompanies, (company) => company.contacts),
            (contact) => contact.students
        )
            .map((student) => student.email)
            .join(',');
        try {
            await navigator.permissions.query({ name: 'clipboard-write' });
            await navigator.clipboard.writeText(textToCopy);
            this.toasterService.success(
                this.translateService.instant(
                    'session-details.student-tab.emailCopiedToClipboard'
                )
            );
        } catch (error) {
            prompt(
                this.translateService.instant(
                    'session-details.student-tab.copyToClipboard'
                ),
                textToCopy
            );
        }
    }

    getGeneratedRoute(): string {
        return (
            '/api/training-sessions/' +
            this.sessionId +
            '/documents.pdf/' +
            this.getGenerateDocsParams()
        );
    }

    sendSelectedMails(template?: string): void {
        let route =
            '/api/training-sessions/' +
            this.sessionId +
            '/send-mails/' +
            this.getSendParams(template);
        this.toasterService.success(
            '⏳ Préparation de(s) mail(s) à envoyer...'
        );
        this.sessionService.sendSelectedDocumentsByMail(route).subscribe(
            () => {
                this.updateSentMails();
                this.toasterService.success(
                    '📤 Mail(s) envoyé(s) avec succès!'
                );
            },
            (err) => {
                this.toasterService.error(
                    'Impossible d’envoyer le(s) mail(s).'
                );
                console.error('Error while sending mail:', err);
            }
        );
    }

    updateStudentMailSent(studentId, mailSent, conventionSent?): void {
        return this.tsCompanies
            .reduce((c, company) => c.concat(company.contacts), [])
            .reduce((c, contact) => c.concat(contact.students), [])
            .filter((student) => student.id == studentId)
            .forEach((student) => {
                student.conventionSent = conventionSent;
                student.mailSent = mailSent;
                return student;
            });
    }

    updateSentMails(): void {
        this.sessionService
            .getRegisteredStudents(this.sessionId)
            .subscribe((registrations) =>
                registrations
                    .reduce(
                        (c, registration) =>
                            c.concat(registration.registrationDetails),
                        []
                    )
                    .forEach((rd) =>
                        this.updateStudentMailSent(
                            rd.student.id,
                            rd.conventionSent || null,
                            rd.mailSent
                        )
                    )
            );
    }

    private getSendParams(template?: string) {
        let res = '?docs=';
        let i = 0;
        this.sendList.forEach((doc) => {
            if (this.dropdownCheckboxes.send[doc.id].checked) {
                res = i === 0 ? (res += doc.id) : (res += ',' + doc.id);
                ++i;
            }
        });

        res += '&registrations=';
        i = 0;
        this.tsCompanies.forEach((company) => {
            company.contacts.forEach((contact) => {
                contact.students.forEach((student) => {
                    if (student.selected === true) {
                        res =
                            i === 0
                                ? (res += student.id)
                                : (res += ',' + student.id);
                        ++i;
                    }
                });
            });
        });

        template = template === undefined ? 'ZENIKA' : template;
        res += '&template=' + template;

        return res;
    }

    sendTrainingSessionRecapToContact() {
        this.sessionService
            .sendTrainingSessionRecapToContact(
                this.session.id,
                this.getContacts()[0].id,
                this.htmlEmail
            )
            .subscribe(
                () => {
                    this.toasterService.success('Message envoyé avec succès !');
                },
                (err) => {
                    console.error('Error while sending mail:', err);
                }
            );
    }

    async initRecapMessageForContact() {
        const contact = this.getContacts()[0];
        const contactFullName = `${
            contact.firstName
        } ${contact.lastName.toUpperCase()}`;
        const startDate = moment(this.session.startDate);
        const startHour = this.session.hours[0].startTime;
        const endHour =
            this.session.hours[1] === undefined
                ? this.session.hours[0].endTime
                : this.session.hours[1].endTime;
        this.htmlEmail = `
                <p>Bonjour ${contactFullName}</p>
                <p>${this.session.trainer}, formateur·rice pour la session "${
            this.session.title
        }",
                sera présent·e sur votre site ${
                    this.session.local
                } à l'adresse ${this.session.trainingSessionExtended.address}
                le ${startDate.format('dddd Do MMMM')}.
                Le formateur·rice se présentera 15 à 30 minutes avant le début de la formation.</p>
                <p>Le point de contact sur place : ${contactFullName}</p>
                <p><b>IMPORTANT HORAIRES</b></p>
                <p>Merci de bien vouloir informer vos collaborateurs des horaires : début de formation ${startHour} fin ${endHour}.</p>
                <p>Voici les conditions nécessaires au bon déroulement de la formation :
                    <ul>
                        <li>1 connexion réseau avec accès internet</li>
                        <li>1 vidéoprojecteur</li>
                        <li>1 tableau blanc ou papier</li>
                        <li>les droits administrateurs sur les postes des stagiaires</li>
                    </ul>
                </p>
                <p>En vous souhaitant une bonne réception et en vous remerciant pour votre aide.</p>
                <p>Cordialement,</p>
                <p>Equipe Zenika Training</p>
            `;
    }

    isRecapAvailable() {
        return (
            this.getContacts().length === 1 &&
            this.session.trainerId &&
            this.session.location !== 'À définir' &&
            this.isIntraSession()
        );
    }

    getContacts() {
        return this.tsCompanies
            .flatMap((company) => company.contacts)
            .filter((contact) => contact.selected);
    }

    isIntraSession() {
        return this.session.type === 'intra';
    }

    getRecapButtonTooltip() {
        return !this.isRecapAvailable()
            ? 'le recap est disponible seulement sur les sessions intra avec formateur et lieu renseignés et un unique contact sélectionné'
            : '';
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/session/detail/registration/document-generation/document-generation.component.ts